import { useEffect, useRef, useState } from "react";
import api, { streamPost } from "@/lib/api";
import StructuredAnswer from "@/components/StructuredAnswer";
import { LegalBeagle } from "@/components/BrandArt";
import STATES from "@/data/states";
import { Send, ArrowRight, Loader2 } from "lucide-react";

const GREETING = {
  federal: "Hi, I’m Leego Beego. How can I help with your federal legal research today?",
  state_local: "Hi, I’m Leego Beego. Pick your state (or just tell me where it happened) and ask your state or local question.",
};

export default function LandingChat({ user, agreed, onEnter }) {
  const [scope, setScope] = useState("federal");
  const [input, setInput] = useState("");
  const [threads, setThreads] = useState({ federal: [], state_local: [] });
  // Bundled list first so the picker works offline; the API refreshes it when reachable.
  const [states, setStates] = useState(STATES);
  const [stateCode, setStateCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sessions = useRef({});
  const sending = useRef(false);
  const statesFetched = useRef(false);
  const available = Boolean(user && agreed);
  const messages = threads[scope];
  const courtHelp = scope === "state_local" ? states.find((s) => s.code === stateCode) : null;

  useEffect(() => {
    if (scope !== "state_local" || statesFetched.current) return;
    statesFetched.current = true;
    api.get("/states").then((r) => { if (r.data?.length) setStates(r.data); }).catch(() => {});
  }, [scope]);

  function chooseState(code) {
    setStateCode(code);
    const sid = sessions.current.state_local;
    if (sid) api.patch(`/chat/sessions/${sid}`, { state: code || null }).catch(() => {});
  }

  function update(fn) {
    setThreads((all) => ({ ...all, [scope]: fn(all[scope]) }));
  }

  async function send(event) {
    event.preventDefault();
    const text = input.trim();
    if (!available || !text || sending.current) return;
    sending.current = true;
    setBusy(true);
    setError("");
    let added = false;
    try {
      if (!sessions.current[scope]) {
        const response = await api.post("/chat/sessions", {
          title: "Leego Beego landing research", jurisdiction_scope: scope,
          state: scope === "state_local" ? (stateCode || null) : null,
        });
        sessions.current[scope] = response.data.session_id;
      }
      update((items) => [...items, { role: "user", content: text }, { role: "assistant", content: "", sources: [] }]);
      added = true;
      setInput("");
      await streamPost("/chat/message", { session_id: sessions.current[scope], text, mode: "research", attachments: [] },
        (delta) => update((items) => items.map((item, i) => i === items.length - 1 ? { ...item, content: item.content + delta } : item)),
        (meta) => update((items) => items.map((item, i) => i === items.length - 1 ? { ...item, sources: meta.sources || [] } : item)));
    } catch (err) {
      setError("Your request could not be completed. Check your connection and account access, then try again.");
      if (added) update((items) => items.slice(0, -2));
      setInput(text);
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  return <section className="hkl-chat hkl-card" id="leego" aria-labelledby="leego-title">
    <div className="hkl-chat-heading">
      <LegalBeagle width="66" height="66" className="shrink-0" />
      <div><h2 id="leego-title">Leego Beego</h2><p>Your AI legal research assistant</p></div>
    </div>
    <div className="hkl-scope-row">
      <div className="hkl-scope" role="group" aria-label="Choose legal jurisdiction">
        <button type="button" aria-pressed={scope === "federal"} disabled={busy} onClick={() => setScope("federal")} title="Federal legal research">Federal</button>
        <button type="button" aria-pressed={scope === "state_local"} disabled={busy} onClick={() => setScope("state_local")} title="State and local legal research for all 50 states and D.C.">State/Local</button>
      </div>
      {scope === "state_local" && <>
        <label className="hkl-sr-only" htmlFor="landing-state">Your state</label>
        <select id="landing-state" className="hkl-state-select" value={stateCode} disabled={busy} onChange={(e) => chooseState(e.target.value)}>
          <option value="">Which state?</option>
          {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </>}
    </div>
    {courtHelp && <p className="hkl-court-help">Official links for {courtHelp.name}: <a href={courtHelp.courts_url} target="_blank" rel="noreferrer">court self-help &amp; forms</a> · <a href={courtHelp.statutes_url} target="_blank" rel="noreferrer">state laws</a></p>}
    <div className="hkl-chat-content" aria-label="Conversation">
      {messages.length === 0 && <p className="hkl-bubble">{GREETING[scope]}</p>}
      {messages.map((message, index) => <div className={`hkl-message hkl-message-${message.role}`} key={index}>
        <span className="hkl-message-label">{message.role === "user" ? "You" : "Leego Beego"}</span>
        {message.role === "assistant" ? (message.content ? <StructuredAnswer content={message.content} sources={message.sources} /> : <span>Checking authority…</span>) : <p>{message.content}</p>}
      </div>)}
    </div>
    {!user && <p className="hkl-chat-help">Accept the agreement, then <button type="button" disabled={!agreed} onClick={onEnter}>sign in to chat <ArrowRight size={13} /></button>.</p>}
    {user && !agreed && <p className="hkl-chat-help">Accept the agreement to start your research.</p>}
    {error && <p className="hkl-error" role="alert">{error}</p>}
    <form onSubmit={send} className="hkl-composer">
      <label className="hkl-sr-only" htmlFor="landing-question">Question for Leego Beego</label>
      <input id="landing-question" value={input} onChange={(e) => setInput(e.target.value)} maxLength={4000} disabled={!available || busy} placeholder={scope === "federal" ? "Ask a federal question…" : "Ask a state or local question…"} />
      <button type="submit" aria-label="Send question" disabled={!available || busy || !input.trim()}>{busy ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}</button>
    </form>
    <p className="hkl-chat-footnote" role="status">{busy ? "Leego Beego is researching…" : "Legal research assistance, not legal advice. Verify before filing."}</p>
  </section>;
}
